import type { Ruta } from "../document/paths";

/**
 * Lo que devuelve `POST /suggestions` del microservicio de IA.
 *
 * Espejo de `ia-microservices/src/suggestions/sugerir.ts`. Los dos repos son
 * independientes; el contrato lo sostienen esta interfaz y las pruebas.
 */

export type Severidad = "alta" | "media" | "baja";

/** Lo que el usuario decidió sobre una sugerencia. */
export type EstadoSugerencia = "pendiente" | "aceptada" | "rechazada" | "editada";

export interface Sugerencia {
  id: string;
  ruta: Ruta;
  seccion: string;
  /** Para saber de qué habla sin abrirla: "Analista senior · Banco del Sur". */
  etiqueta: string;
  motivo: string;
  /** Frase para el usuario. La compone el backend desde el motivo, no la IA. */
  razon: string;
  severidad: Severidad;
  motor: "reglas" | "ia";
  original: string;
  sugerido: string;
  /** No hay texto que proponer: solo se avisa de que falta algo. */
  aviso: boolean;
  estado: EstadoSugerencia;
  /** Lo que quedó si el usuario editó la sugerencia en vez de aceptarla. */
  final?: string;
}

export interface RespuestaSugerencias {
  cv_id: number;
  generado: string;
  version_motor: string;
  stats: {
    reglas: number;
    llamadas_ia: number;
    descartados_ia: number;
    ms_ia: number;
    ms_total: number;
  };
  /** Presente si la IA falló. Las deterministas llegan igualmente. */
  aviso?: string;
  sugerencias: Sugerencia[];
}

const ORDEN: Record<Severidad, number> = { alta: 0, media: 1, baja: 2 };

export interface GrupoSugerencias {
  seccion: string;
  etiqueta: string;
  sugerencias: Sugerencia[];
}

const ETIQUETA_SECCION: Record<string, string> = {
  contacto: "Datos personales",
  perfil: "Perfil profesional",
  experiencia: "Experiencia laboral",
  educacion: "Educación",
  competencias: "Competencias",
  idiomas: "Idiomas",
  otros: "Otros",
};

/**
 * Agrupa por sección y ordena por severidad dentro de cada una.
 *
 * El orden de las secciones es el del documento, no el de severidad: el panel
 * tiene que leerse en paralelo a la hoja de vida, y saltar de "Perfil" a
 * "Competencias" y volver obliga a buscar dónde estaba cada cosa.
 */
export const agrupar = (sugerencias: Sugerencia[]): GrupoSugerencias[] => {
  const orden = ["contacto", "perfil", "experiencia", "educacion", "competencias", "idiomas", "otros"];
  const porSeccion = new Map<string, Sugerencia[]>();

  for (const s of sugerencias) {
    const lista = porSeccion.get(s.seccion) ?? [];
    lista.push(s);
    porSeccion.set(s.seccion, lista);
  }

  return orden
    .filter((sec) => porSeccion.has(sec))
    .map((sec) => ({
      seccion: sec,
      etiqueta: ETIQUETA_SECCION[sec] ?? sec,
      sugerencias: (porSeccion.get(sec) ?? []).sort(
        (a, b) => ORDEN[a.severidad] - ORDEN[b.severidad],
      ),
    }));
};

/**
 * Rutas del documento cuyo valor es una LISTA, no un texto.
 *
 * `Sugerencia.sugerido` siempre es texto porque el comparador necesita algo que
 * poder diffear palabra a palabra. Para estos campos hay que convertirlo de
 * vuelta antes de escribirlo: aceptar una sugerencia de herramientas dejaba
 * "Excel, SQL" como cadena donde el documento espera ["Excel","SQL"], y el
 * renderizador reventaba con `join is not a function` -- pantalla en blanco.
 */
const RUTAS_DE_LISTA = new Set(["skills", "soft_skills", "tools", "links"]);

/** El valor a escribir en el documento para una sugerencia dada. */
export const valorParaRuta = (ruta: Ruta, texto: string): string | string[] => {
  if (!RUTAS_DE_LISTA.has(ruta)) return texto;
  return texto
    .split(/[,·]/)
    .map((x) => x.trim())
    .filter(Boolean);
};

export const pendientes = (s: Sugerencia[]): Sugerencia[] =>
  s.filter((x) => x.estado === "pendiente");

/** Las rutas que llevan marca en el documento: solo las sin revisar. */
export const rutasMarcadas = (s: Sugerencia[]): Set<Ruta> =>
  new Set(pendientes(s).map((x) => x.ruta));
